import { Incident } from "../Interfaces"
import api from "./api"
import { AxiosError } from "axios"
import { AddIncidentNote, ResolveIncident } from "../Interfaces";

class IncidentService {
  private baseUrl = "/incident";

  public create = async (incidentInfo: any) => {
    if (!incidentInfo) throw new Error("No incident info provided")
    try {
      const response = await api.post(
        `${this.baseUrl}`,
        incidentInfo,
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return null;
    }
  }

  public getOne = async (id: number) => {
    if (!id) throw new Error("No id provided")
    try {
      const response = await api.get(
        `${this.baseUrl}/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return null;
    }
  }

  public getAllByLocation = async (params: { locationId: number; unresolvedOnly: boolean }): Promise<{ incidents: Incident[], totalCount: number, unresolvedCount: number, page: number, limit: number, totalPages: number }> => {
    try {
      const response = await api.get(
        `${this.baseUrl}/by-location/${params.locationId}?unresolvedOnly=${params.unresolvedOnly}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return { incidents: [], totalCount: 0, unresolvedCount: 0, page: 1, limit: 10, totalPages: 0 };
    }
  }

  public getSomeByLocation = async (locationId: number, limit: number, page: number, searchTerm?: string) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/some/by-location/${locationId}?limit=${limit}&offset=${page}&searchTerm=${searchTerm || ""}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
    }
  }

  public update = async (incidentInfo: any) => {
    try {
      const response = await api.put(
        `${this.baseUrl}`,
        incidentInfo
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return null;
    }
  }

  public delete = async (id: number) => {
    if (!id) throw new Error("No id provided")
    try {
      const response = await api.delete(
        `${this.baseUrl}/${id}`
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return null;
    }
  }

  // CREATE ==============================================
  public addNote = async (incidentInfo: AddIncidentNote,) => {
    try {
      if (!incidentInfo || !incidentInfo.note || !incidentInfo.userId) throw new Error("Missing incident info")
      const response = await api.post(
        `${this.baseUrl}/note`,
        incidentInfo,
      );
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        throw Error(error)
      }
    }
  }

  public getAll = async (searchTerm?: string): Promise<Incident[]> => {
    try {
      const response = await api.get(
        `${this.baseUrl}?searchTerm=${searchTerm || ""}`
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw new Error("Failed to fetch incidents");
      } else {
        console.error(error);
        throw new Error("An unexpected error occurred");
      }
    }
  }

  // UPDATE ==============================================
  public resolve = async (incidentInfo: ResolveIncident) => {
    try {
      if (!incidentInfo || !incidentInfo.incidentId) throw new Error("Missing incident info")
      const response = await api.put(
        `${this.baseUrl}/${incidentInfo.incidentId}/resolve`,
        { resolved: incidentInfo.resolved, resolvedAt: incidentInfo.resolvedAt }
      );
      return response.data
    } catch (error: any) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        throw Error(error)
      }
    }
  }

  public getUserLocations = async (userId: number, managementCompanyId?: number) => {
    try {
      const response = await api.get(
        `${this.baseUrl}/user-locations/${userId}`,
        { params: managementCompanyId ? { managementCompanyId } : undefined },
      );
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(error.response?.statusText)
      } else {
        console.error(error);
      }
      return [];
    }
  }
}

export const incidentService = new IncidentService()
